import { Paragraph, Strong } from '@customerio/pluma-components/react';
import { SegmentConfirmModal, type SegmentModalKind } from './segment-confirm-modal';

/** Which confirmation is open, if any. */
export type SegmentModal = SegmentModalKind | null;

type SegmentConnectionModalsProps = {
  openModal: SegmentModal;
  /** The Segment source the connection was made against, named in the copy. */
  connectedSource: string | null;
  isBusy: boolean;
  /** `journeys/segment_integration/edit` — disable and clear. */
  canEdit: boolean;
  /** `journeys/segment_integration/delete` — delete only. */
  canDelete: boolean;
  onClose: () => void;
  onConfirmDisable: () => void;
  onConfirmDelete: () => void;
  onConfirmClear: () => void;
};

/**
 * The three confirmations, which do three different things.
 *
 * Disable and delete both act on the destination in the reader's Segment workspace
 * and so re-authorize. Clear only forgets the stored connection here, which is why
 * its copy promises no Segment data is touched — it is the way out when the Segment
 * side has been changed or removed and the other two can no longer reach it.
 */
export function SegmentConnectionModals({
  openModal,
  connectedSource,
  isBusy,
  canEdit,
  canDelete,
  onClose,
  onConfirmDisable,
  onConfirmDelete,
  onConfirmClear,
}: SegmentConnectionModalsProps) {
  const source = connectedSource ?? 'this connection';
  const shared = { isBusy, onClose };

  return (
    <>
      <SegmentConfirmModal
        {...shared}
        kind="delete"
        isOpen={openModal === 'delete'}
        title="Are you sure you want to delete this connection?"
        confirmLabel="Yes, delete connection"
        isAllowed={canDelete}
        onConfirm={onConfirmDelete}
      >
        <Paragraph mb="200">
          Deleting <Strong>{source}</Strong> will stop that Segment Source's data from coming to Customer.io, and delete
          any Segment settings related to it. <Strong>This cannot be undone.</Strong>
        </Paragraph>
      </SegmentConfirmModal>

      <SegmentConfirmModal
        {...shared}
        kind="disable"
        isOpen={openModal === 'disable'}
        title="Are you sure you want to disable this connection?"
        confirmLabel="Yes, disable connection"
        isAllowed={canEdit}
        onConfirm={onConfirmDisable}
      >
        <Paragraph mb="200">
          Disabling <Strong>{source}</Strong> will stop that Segment Source's data from coming to Customer.io.
        </Paragraph>
      </SegmentConfirmModal>

      <SegmentConfirmModal
        {...shared}
        kind="clear"
        isOpen={openModal === 'clear'}
        title="Clear this connection"
        confirmLabel="Clear connection"
        isAllowed={canEdit}
        onConfirm={onConfirmClear}
      >
        <Paragraph mb="200">
          Clearing your connection allows you to reconnect to Segment from this page. Do this if a Segment Source or
          Workspace has been changed or deleted from within Segment.
        </Paragraph>
        <Paragraph mb="200" weight="medium">
          Note that clearing your connection will not delete any Segment data, or stop any Segment data from coming into
          Customer.io.
        </Paragraph>
      </SegmentConfirmModal>
    </>
  );
}
